import type { Ingredient } from '@cf-shared/data-model/ingredient';
import type { Log_Meal } from '@cf-shared/data-model/log-meal';

// Reading a meal's ingredient list defensively.
//
// Two callers, deliberately reporting at different severities. The read-path
// normaliser in user-db.service.ts calls this as a meal comes off disk, where an
// older app version is the plausible writer and the repair has provably just
// run, so it reports at info level. cs-day-items.ts calls it while rendering a
// meal already in memory, where the known cause was a live bug emptying the
// object after it was read, so it reports that as a problem. See
// design-docs/telemetry.md for the severity rule both are applying.
//
// `Log_Meal.ingredients` is declared as a plain `Ingredient[]`, so TypeScript
// never asks a reader to check it. A meal that reached the app with no list at
// all is therefore invisible to the compiler and lethal at runtime: every read
// of `.length` throws, and the two reads the template makes on every
// change-detection pass throw again on every retry. In #4500 that produced 50
// `TypeError: Cannot read properties of undefined (reading 'length')` in 12
// seconds, and what the customer saw was one of her meals simply gone.
//
// The repair is deliberately NOT a silent null check. CLAUDE.md's "unexpected
// conditions must surface" rule means a meal in an impossible state has to be
// reported, and its "keep every guard against a damaged or old-shaped document"
// rule means it also has to be normalised rather than allowed to crash. So this
// does both: it repairs the meal and tells the caller it had to, and the caller
// turns that into one report naming the meal.
//
// Pure, and in its own module, so the rules can be unit-tested without
// instantiating Angular — mocha's ts-node register cannot load a file that
// transitively pulls in @angular/core's ESM exports. The same constraint, and
// the same answer, as cs-day-items-load-recovery.ts and
// cs-day-items-update-card.ts next to that component.

export interface MealIngredientsRead {
  /** the meal's ingredient list, always a real array */
  ingredients: Ingredient[];
  /**
   * True only on the FIRST read of a damaged meal. The repair below writes an
   * empty array onto the meal, so every later read of the same object finds one
   * and answers false. That is what turns a storm of identical reports into
   * exactly one per damaged meal.
   */
  wasMissing: boolean;
}

/**
 * Answers with the meal's ingredient list. When the list is missing or is not an
 * array, repairs the meal in place with an empty list and says so.
 *
 * The repair is in place rather than detached on purpose. Callers do not only
 * read this list — some of them push into it, slice it or replace it — so they
 * need a real array sitting on the meal, not a copy that is thrown away.
 */
export function readMealIngredients(meal: Log_Meal): MealIngredientsRead {
  // if the meal already carries a real list, hand it back untouched
  if (Array.isArray(meal.ingredients)) {
    return { ingredients: meal.ingredients, wasMissing: false };
  }
  // otherwise repair the meal so every later reader finds a list, and say we had to
  meal.ingredients = [];
  return { ingredients: meal.ingredients, wasMissing: true };
}

/**
 * The read-path version: repair the meal, and answer whether this repair is worth
 * reporting given what has already been reported this session.
 *
 * `readMealIngredients` alone cannot answer that here. It stays quiet on later reads
 * because the repair sits on the object it repaired — but the read path re-reads the
 * same document off disk as a NEW object on every backlog rebuild, so each rebuild
 * would look like a first read and report again. `alreadyReported` carries the answer
 * across those objects; the caller owns it and this function adds to it.
 *
 * Separate from the service so the rule can be unit-tested: user-db.service.ts pulls in
 * @angular/core, which mocha's ts-node register cannot load.
 */
export function repairMealIngredientsFromStorage(meal: Log_Meal, alreadyReported: Set<string>): { shouldReport: boolean } {
  const read = readMealIngredients(meal);
  // if the meal was fine, or this same meal has already been reported, say nothing
  if (!read.wasMissing || alreadyReported.has(meal._id)) {
    return { shouldReport: false };
  }
  alreadyReported.add(meal._id);
  return { shouldReport: true };
}
